import { useEffect, useState } from 'react';
import { taskErrorCode, taskErrorMessage } from '../../services/TaskTrackingService';
import { completeResultLine, sameStageLanguages, shortName } from './taskUi';
import { pendingCompletions } from './taskCompletions';
import { refreshTaskSummary } from './useTaskSummary';

/**
 * Completing the current stage: what happens next, who's handed it, the note, and the warnings
 * first. The step card shows it, and Undo comes from pendingCompletions.
 */
const useCompleteStep = ({
    open,
    taskId,
    detail,
    current,
    link,
    linkedLevel,
    users,
    postSection,
    postSaved,
    applied,
    load,
    onChanged,
    setSnack,
    busy,
    isAssignee,
    focusAction,
    completeBlocked,
    needsDubbingAudio,
    converting,
}) => {
    const [handoff, setHandoff] = useState(null);
    const [grantHandoffAccess, setGrantHandoffAccess] = useState(false);
    const [completeNote, setCompleteNote] = useState('');
    const [noteOpen, setNoteOpen] = useState(false);
    // A PM not on the step goes straight to Stages, but can still complete it for someone
    const [opened, setOpened] = useState(false);
    // "Ramesh is editing Gujarati now" confirm before a linked Complete
    const [confirmEditing, setConfirmEditing] = useState(false);
    const [softWarning, setSoftWarning] = useState(null);
    const [sendParts, setSendParts] = useState([]);
    // Languages untaken from Complete for several languages; every one starts ticked
    const [unticked, setUnticked] = useState([]);
    // What Complete does: 'next' (hand on), 'finish' (skip an optional next stage) or 'back:STAGE' (QC)
    const [choice, setChoice] = useState('next');

    const task = detail?.task;
    const perms = detail?.permissions || {};
    const stages = detail?.stages || [];
    const v = () => ({ version: task.version });

    useEffect(() => {
        if (open) {
            setCompleteNote('');
            setNoteOpen(false);
            setOpened(false);
            setHandoff(null);
            setGrantHandoffAccess(false);
        }
    }, [open, taskId]);

    const nextStage = current
        ? stages.find((s) => s.order > current.order && s.status !== 'COMPLETED' && s.status !== 'SKIPPED')
        : null;
    const handoffLocked = !!nextStage?.plannedAssignee && !perms.canManage;

    useEffect(() => {
        if (nextStage) {
            setHandoff(nextStage.plannedAssignee || nextStage.assignedTo || null);
        }
    }, [nextStage?.stageKey, nextStage?.plannedAssignee, nextStage?.assignedTo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Final check: finishing is the default unless the PM planned someone for Post check.
    // QC: nothing is picked, since most rounds go back and passing by accident is the worse mistake.
    const canSkipNext = !!detail?.nextOptional && !!nextStage;
    const hasBackTargets = (detail?.sendBackTargets || []).length > 0;
    useEffect(() => {
        setChoice(canSkipNext ? (nextStage?.plannedAssignee ? 'next' : 'finish') : hasBackTargets ? '' : 'next');
        setSendParts([]);
        setUnticked([]);
        setNoteOpen(false);
    }, [task?.id, current?.stageKey, canSkipNext, hasBackTargets]); // eslint-disable-line react-hooks/exhaustive-deps

    const stageByKey = (key) => stages.find((s) => s.stageKey === key);
    const backTargets = (detail?.sendBackTargets || []).map(stageByKey).filter(Boolean);
    const sendingBackTo = choice.startsWith('back:') ? stageByKey(choice.slice(5)) : null;
    const finishing = choice === 'finish' && canSkipNext;
    // Stages between the target and this one that get done again (Dubbing: Audio conversion, Editing)
    const redoneWith = (target) =>
        stages
            .filter((s) => s.order > target.order && s.order < current.order && s.status === 'COMPLETED')
            .map((s) => s.displayName);
    const listNames = (names) =>
        names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    const completeChoices = current
        ? [
              ...(canSkipNext
                  ? [
                        {
                            value: 'finish',
                            label: 'Finish the task',
                            help: `${nextStage.displayName} isn't needed and is marked skipped.`,
                        },
                        { value: 'next', label: `Move to ${nextStage.displayName}` },
                    ]
                  : []),
              ...(backTargets.length > 0
                  ? [
                        {
                            value: 'next',
                            label: nextStage ? `Passed. Hand ${nextStage.displayName} on` : 'Passed. Finish the task',
                        },
                        ...backTargets.map((t) => {
                            const redone = redoneWith(t);
                            return {
                                value: `back:${t.stageKey}`,
                                label: `Back to ${t.displayName}${t.assignedTo ? ` (${shortName(t.assignedTo)})` : ''}`,
                                help: redone.length ? `${listNames(redone)} will be done again too.` : undefined,
                            };
                        }),
                    ]
                  : []),
          ]
        : [];

    // A warning, never a block (D54). Empty boxes at the text stages, no new file at Dubbing and Replication
    const postWarning = () => {
        const post = detail.post;
        if (!post || !current) return null;
        const label = task.languageLabel;
        if (['TRANSLATE_TEXT', 'TEXT_QC'].includes(current.stageKey) && task.languageKey !== 'eng') {
            const { design, caption } = postSection.current?.values() || {};
            const empty = [
                post.englishDesign && !post.subtitleVideo && !(design || '').trim() ? 'Text on design' : null,
                post.englishCaption && !(caption || '').trim() ? 'Caption' : null,
            ].filter(Boolean);
            const verb = empty.length > 1 ? 'are' : 'is';
            return empty.length
                ? {
                      title: 'Empty box',
                      body: `${empty.join(' and ')} ${verb} empty for ${label}. Complete anyway?`,
                      text: `${empty.join(' and ')} ${verb} empty`,
                  }
                : null;
        }
        if (['DUBBING', 'REPLICATION'].includes(current.stageKey) && detail.workFolder?.stageFiles === 0) {
            return {
                title: 'No files yet',
                body: `${label} has no new file in the shared folder. Complete anyway?`,
                text: 'no files yet',
            };
        }
        return null;
    };

    // Complete for several languages (D47). At QC it covers Passed; a send-back needs its own comment
    const several = detail?.post && current && perms.canComplete && !sendingBackTo ? sameStageLanguages(detail.post, current.stageKey, task.id) : [];
    const picked = several.filter((e) => !unticked.includes(e.languageKey));
    const completingSeveral = several.length > 1 && !(picked.length === 1 && picked[0].taskId === task.id);

    const severalWarning = () => {
        const own = postWarning();
        const lines = picked
            .map((e) => [e.languageLabel, e.taskId === task.id ? own?.text : e.warning])
            .filter(([, warning]) => warning)
            .map(([language, warning]) => `${language}: ${warning}.`);
        return lines.length ? { title: 'Not ready yet', body: `${lines.join(' ')} Complete anyway?`, several: true } : null;
    };

    const completeSeveral = () => {
        const note = completeNote || undefined;
        const items = picked.map((e) => ({
            taskId: e.taskId,
            body: { version: e.taskId === task.id ? task.version : e.version, note },
        }));
        const name = current.displayName;
        const labels = Object.fromEntries(picked.map((e) => [e.taskId, e.languageLabel]));
        pendingCompletions.scheduleMany(items).then((results) => {
            const failed = results.filter((r) => r.error);
            const done = results.filter((r) => r.result && !r.result.undone);
            if (failed.length === 0 && done.length === 0) return;
            load();
            onChanged && onChanged();
            refreshTaskSummary();
            if (failed.length > 0) {
                setSnack({
                    message: completeResultLine(
                        name,
                        done.length,
                        failed.map((r) => ({ label: labels[r.taskId], message: taskErrorMessage(r.error) })),
                    ),
                    error: true,
                });
            }
        });
        setSnack({ message: `${name} completed for ${items.length} language${items.length === 1 ? '' : 's'}`, undoTaskIds: items.map((i) => i.taskId) });
    };

    const complete = () => {
        const note = completeNote || undefined;
        const body = sendingBackTo
            ? { ...v(), note, sendBackTo: sendingBackTo.stageKey, parts: sendParts.length ? sendParts : undefined }
            : finishing
              ? { ...v(), note, skipNext: true }
              : {
                    ...v(),
                    note,
                    nextAssignee: handoffLocked ? undefined : handoff || undefined,
                    grantContentAccess: grantHandoffAccess,
                };
        const id = task.id;
        const label = sendingBackTo
            ? `Sent back to ${sendingBackTo.displayName}${sendingBackTo.assignedTo ? ` (${shortName(sendingBackTo.assignedTo)})` : ''}`
            : finishing
              ? `${current.displayName} completed. Task finished.`
              : linkedLevel && !link.editorPastLevel
                ? `${current.displayName} completed. ${linkedLevel} will be submitted in the editor.`
                : `${current?.displayName || 'Stage'} completed`;
        pendingCompletions
            .schedule(id, body)
            .then((result) => {
                if (result && !result.undone && !result.flushed) applied(result);
            })
            .catch((e) => {
                setSnack({ message: taskErrorMessage(e), error: true });
                // A failed editor submit flags the task; show that
                if (taskErrorCode(e) === 'EDITOR_WRITE_FAILED') load();
            });
        setSnack({ message: label, undoTaskIds: [id] });
    };

    // Someone else has the language open: their unsaved changes won't be in the submission.
    const requestComplete = async () => {
        if (!(await postSaved())) return;
        if (sendingBackTo) {
            complete();
            return;
        }
        if (linkedLevel && link.activeEditor && !link.editorPastLevel) {
            setConfirmEditing(true);
            return;
        }
        const warning = completingSeveral ? severalWarning() : postWarning();
        if (warning) {
            setSoftWarning(warning);
            return;
        }
        if (completingSeveral) {
            completeSeveral();
            return;
        }
        complete();
    };

    const handoffOptions = users.filter((u) => perms.canManage || u.canEdit);
    const handoffUser = users.find((u) => u.email === handoff);

    const stepOpen = !!current && (isAssignee || opened || focusAction === 'complete');
    const completeButton = current &&
        perms.canComplete &&
        stepOpen && {
            label: sendingBackTo
                ? `Send back to ${sendingBackTo.displayName}`
                : completingSeveral
                  ? `Complete ${current.displayName} for ${picked.length} language${picked.length === 1 ? '' : 's'}`
                  : !choice
                    ? `Complete ${current.displayName}`
                    : finishing
                      ? 'Complete task'
                      : linkedLevel
                        ? `Complete ${current.displayName}`
                        : nextStage
                          ? `Complete${handoff && !handoffLocked ? ` & hand to ${shortName(handoff)}` : ''}`
                          : 'Complete task',
            color: sendingBackTo ? 'warning' : 'success',
            icon: !sendingBackTo,
            disabled:
                busy ||
                !choice ||
                (!!sendingBackTo && !completeNote.trim()) ||
                (!sendingBackTo && (!!completeBlocked || needsDubbingAudio)) ||
                pendingCompletions.isPending(task.id) ||
                (several.length > 1 && picked.length === 0) ||
                (!sendingBackTo &&
                    !finishing &&
                    !completingSeveral &&
                    perms.canManage &&
                    !!handoffUser &&
                    !handoffUser.canEdit &&
                    !grantHandoffAccess),
            onClick: requestComplete,
        };
    // What stops Complete, which beats any other hint
    const dubbingLine =
        needsDubbingAudio && !sendingBackTo
            ? converting
                ? 'Complete opens when the dubbing audio is ready.'
                : `Complete opens once the ${task.languageLabel} shared folder has the dubbing audio. Make it with New Dubbing Audio, or upload it to the folder.`
            : null;
    const blockedHint = completeButton ? (!sendingBackTo && completeBlocked) || dubbingLine || null : null;
    const submitHint =
        completeButton && linkedLevel && !completeBlocked && !link.editorPastLevel && !sendingBackTo
            ? `This also submits ${linkedLevel} in the editor.`
            : null;

    return {
        stepOpen,
        openStep: () => setOpened(true),
        completeButton,
        blockedHint,
        submitHint,
        noteOpen,
        setNoteOpen,
        nextStage,
        handoffLocked,
        handoff,
        setHandoff,
        handoffOptions,
        handoffUser,
        grantHandoffAccess,
        setGrantHandoffAccess,
        completeNote,
        setCompleteNote,
        choice,
        setChoice,
        completeChoices,
        sendingBackTo,
        finishing,
        sendParts,
        setSendParts,
        several,
        picked,
        completingSeveral,
        unticked,
        setUnticked,
        complete,
        completeSeveral,
        softWarning,
        setSoftWarning,
        confirmEditing,
        setConfirmEditing,
    };
};

export default useCompleteStep;
